/** @jsxImportSource @emotion/react */
import React, { useMemo, useRef } from 'react'
import { css, Global } from '@emotion/react'
import { DatePicker, Select, Tooltip } from 'antd'
const { Option } = Select
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CopyOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { formatSlotDuration } from '../../utils/timeRangeValidation'
import TimePickerWithInterval from 'src/components/ts-components/TimePickerWithInterval'
import type { TimeSlotRowProps } from './types'
import { COLUMN_WIDTHS } from './types'
import DayOfWeekSelector from './DayOfWeekSelector'
import {
  DAYS_OF_WEEK,
  RECURRENCE_TYPES,
} from '../../hooks/useSchedulerTimeSlots'

// Helper to format a date-specific slot's date for display
const formatSlotDate = (dateStr: string): string => {
  const d = dayjs(dateStr)
  return d.isValid() ? d.format('MMM D') : dateStr
}

// Helper to get day name from date string
const getDayNameFromDate = (dateStr: string): string => {
  const d = dayjs(dateStr)
  return d.isValid() ? d.format('dddd') : '---'
}

const TIME_SLOT_ROW_PICKER_CLASS = 'time-slot-row-picker-popup'
const fullWidthStyle = { width: '100%' } as const
const iconFontStyle = { fontSize: 18 } as const
const darkDropdownStyle = { backgroundColor: '#212935' } as const

// Helper to format days for display
const formatDaysDisplay = (days: number[]): string => {
  if (!days || days.length === 0) {
    return '---'
  }
  if (days.length === 7) {
    return 'Everyday'
  }
  if (
    days.length === 5 &&
    days.includes(1) &&
    days.includes(2) &&
    days.includes(3) &&
    days.includes(4) &&
    days.includes(5)
  ) {
    return 'Weekdays'
  }
  if (days.length === 2 && days.includes(0) && days.includes(6)) {
    return 'Weekends'
  }
  return days
    .sort((a, b) => a - b)
    .map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.short || '')
    .join(', ')
}

// Helper to calculate duration (overnight-aware; '00:00:00' for invalid ranges)
const calculateDuration = (startTime: string, endTime: string): string =>
  formatSlotDuration(startTime, endTime, {
    withSeconds: true,
    invalidPlaceholder: '00:00:00',
  })

// Helper to format time for display (12hr format)
const formatTimeDisplay = (time24: string): string => {
  if (!time24) {
    return '00:00 AM'
  }
  const [hours, minutes] = time24.split(':').map(Number)
  const period = hours >= 12 ? 'PM' : 'AM'
  const hours12 = hours % 12 || 12
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
}

// Helper to parse time from API format
const parseTimeFromSlot = (
  timeRanges: { start_time: string; end_time: string }[]
): { start: string; end: string } => {
  if (!timeRanges || timeRanges.length === 0) {
    return { start: '00:00', end: '00:00' }
  }
  // Use first time range
  const range = timeRanges[0]
  return {
    start: range.start_time?.slice(0, 5) || '00:00',
    end: range.end_time?.slice(0, 5) || '00:00',
  }
}

/**
 * Time slot row component - handles both display and edit modes
 */
const TimeSlotRow: React.FC<TimeSlotRowProps> = ({
  timeSlot,
  index,
  isEditing,
  editState,
  divisions,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onDuplicate,
  onEditStateChange,
  loading,
}) => {
  const divisionsSelectRef = useRef<any>(null)
  const slotNumber = (index + 1).toString().padStart(4, '0')
  const times = useMemo(
    () => parseTimeFromSlot(timeSlot.time_ranges),
    [timeSlot.time_ranges]
  )

  // Common text style
  const textStyles = css`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20.3px;
    color: ${colors.NEUTRAL_0};
    overflow: hidden;
    text-overflow: ellipsis;
  `

  // DatePicker styles (reused for DatePicker only)
  const datePickerStyles = css`
    height: 36px;
    background-color: ${'#212935'};
    border: 1px solid ${'#313b4b'};
    border-radius: 8px;

    input {
      color: ${colors.NEUTRAL_0} !important;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
    }

    &:hover,
    &.ant-picker-focused {
      border-color: ${colors.PRIMARY};
    }

    .ant-picker-suffix {
      color: ${colors.NEUTRAL_50};
    }

    .ant-picker-clear {
      background: ${'#212935'};
      color: ${colors.NEUTRAL_50};
    }
  `

  // Select dropdown styles
  const selectStyles = css`
    .ant-select-selector {
      background-color: ${'#212935'} !important;
      border: 1px solid ${'#313b4b'} !important;
      border-radius: 8px !important;
      height: 36px !important;
      overflow: hidden;
      padding: 2px 6px !important;
      color: ${colors.NEUTRAL_0} !important;
    }

    .ant-select-selection-item {
      line-height: 30px !important;
      color: ${colors.NEUTRAL_0} !important;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
    }

    .ant-select-selection-placeholder {
      color: ${colors.NEUTRAL_50} !important;
      line-height: 30px !important;
    }

    .ant-select-arrow {
      color: ${colors.NEUTRAL_0};
    }

    /* Multi-select tag styles */
    &.ant-select-multiple .ant-select-selection-item {
      background-color: ${colors.SECONDARY_300} !important;
      border: 1px solid ${'#313b4b'} !important;
      border-radius: 4px !important;
      color: ${colors.NEUTRAL_0} !important;
      height: 28px !important;
      line-height: 26px !important;
    }

    &.ant-select-multiple .ant-select-selection-item-remove {
      color: ${colors.NEUTRAL_50} !important;
      &:hover {
        color: ${colors.NEUTRAL_0} !important;
      }
    }

    &:hover .ant-select-selector {
      border-color: ${colors.PRIMARY} !important;
    }

    &.ant-select-focused .ant-select-selector {
      border-color: ${colors.PRIMARY} !important;
      box-shadow: 0 0 0 2px ${colors.PRIMARY}33 !important;
    }

    .ant-select-clear {
      background: ${'#212935'};
      color: ${colors.NEUTRAL_50};
    }
  `

  const iconButtonStyles = css`
    background: transparent;
    border: none;
    color: ${colors.PRIMARY};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    &:hover {
      color: ${colors.PRIMARY_HOVER};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `

  if (isEditing && editState) {
    // Edit mode
    return (
      <>
        <Global
          styles={css`
            .dark-select-popup.ant-select-dropdown,
            .dark-select-popup .ant-select-dropdown {
              background-color: ${colors.SECONDARY_400} !important;
              border: 1px solid ${colors.SECONDARY_200} !important;
            }

            .dark-select-popup .ant-select-item,
            .dark-select-popup .ant-select-item-option-content {
              color: ${colors.WHITE} !important;
            }

            .dark-select-popup
              .ant-select-item-option-active:not(
                .ant-select-item-option-disabled
              ) {
              background-color: ${colors.SECONDARY_300} !important;
            }

            .dark-select-popup
              .ant-select-item-option-selected:not(
                .ant-select-item-option-disabled
              ) {
              background-color: ${colors.PRIMARY} !important;
            }

            .dark-select-popup
              .ant-select-item-option-selected:not(
                .ant-select-item-option-disabled
              )
              .ant-select-item-option-content {
              color: ${colors.SECONDARY_500} !important;
            }

            .${TIME_SLOT_ROW_PICKER_CLASS} {
              .ant-picker-panel-container {
                background-color: ${colors.SECONDARY_300};
                border: 1px solid ${colors.SECONDARY_200};
                border-radius: 8px;
                overflow: hidden;
              }

              .ant-picker-panel {
                background-color: ${colors.SECONDARY_300};
                border: none;
              }

              .ant-picker-header {
                color: ${colors.WHITE};
                border-bottom: 1px solid ${colors.SECONDARY_200};
              }

              .ant-picker-cell {
                color: ${colors.NEUTRAL_50};
              }

              .ant-picker-cell-in-view {
                color: ${colors.NEUTRAL_0};
              }

              .ant-picker-cell:hover:not(.ant-picker-cell-selected)
                .ant-picker-cell-inner {
                background-color: ${colors.SECONDARY_400} !important;
              }

              .ant-picker-cell-selected .ant-picker-cell-inner {
                background-color: ${colors.PRIMARY} !important;
                color: ${colors.PRIMARY_TEXT} !important;
              }

              .ant-picker-cell-today .ant-picker-cell-inner::before {
                border-color: ${colors.PRIMARY} !important;
              }

              .ant-picker-header button {
                color: ${colors.NEUTRAL_50};

                &:hover {
                  color: ${colors.NEUTRAL_0};
                }
              }

              .ant-picker-super-prev-icon,
              .ant-picker-prev-icon,
              .ant-picker-next-icon,
              .ant-picker-super-next-icon {
                color: ${colors.NEUTRAL_50};
              }
            }
          `}
        />
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 8px 16px 8px 45px;
            background-color: ${'#212935'};
            border-left: 3px solid ${colors.PRIMARY};
            width: 100%;
          `}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onCancel()
            }
          }}
        >
          {/* Slot number */}
          <span
            css={css`
              ${textStyles}
              width: ${COLUMN_WIDTHS.SLOT}px;
              color: ${colors.NEUTRAL_0};
              font-weight: 400;
            `}
          >
            {slotNumber}
          </span>

          {/* Frequency dropdown */}
          <Select
            value={editState.frequency}
            onChange={(value) => {
              if (value === 'specific') {
                onEditStateChange({
                  ...editState,
                  frequency: value,
                  days: [],
                  slotDate: editState.slotDate || null,
                })
              } else {
                onEditStateChange({
                  ...editState,
                  frequency: value,
                  slotDate: null,
                  days: editState.days.length > 0 ? editState.days : [1, 3, 5],
                })
              }
            }}
            disabled={loading}
            css={selectStyles}
            style={{ width: COLUMN_WIDTHS.FREQUENCY }}
            dropdownStyle={darkDropdownStyle}
            options={RECURRENCE_TYPES.map((rt) => ({
              value: rt.value,
              label: rt.label,
            }))}
          />

          {/* Days of week selector OR date picker */}
          <div
            css={css`
              width: ${COLUMN_WIDTHS.DAYS}px;
            `}
          >
            {editState.frequency === 'specific' ? (
              <DatePicker
                value={
                  editState.slotDate
                    ? dayjs(editState.slotDate, 'YYYY-MM-DD')
                    : null
                }
                onChange={(value) =>
                  onEditStateChange({
                    ...editState,
                    slotDate: value ? value.format('YYYY-MM-DD') : null,
                  })
                }
                format='MMM D, YYYY'
                disabled={loading}
                css={datePickerStyles}
                popupClassName={TIME_SLOT_ROW_PICKER_CLASS}
                style={fullWidthStyle}
                placeholder='Select date'
              />
            ) : (
              <DayOfWeekSelector
                selectedDays={editState.days}
                onChange={(days) => onEditStateChange({ ...editState, days })}
                disabled={loading}
              />
            )}
          </div>

          {/* Start time */}
          <div
            css={css`
              width: ${COLUMN_WIDTHS.START_TIME}px;
            `}
          >
            <TimePickerWithInterval
              value={
                editState.startTime ? dayjs(editState.startTime, 'HH:mm') : null
              }
              onChange={(value) =>
                onEditStateChange({
                  ...editState,
                  startTime: value
                    ? value.format('HH:mm')
                    : editState.startTime,
                })
              }
              disabled={loading}
              style={fullWidthStyle}
            />
          </div>

          {/* End time */}
          <div
            css={css`
              width: ${COLUMN_WIDTHS.END_TIME}px;
            `}
          >
            <TimePickerWithInterval
              value={
                editState.endTime ? dayjs(editState.endTime, 'HH:mm') : null
              }
              onChange={(value) =>
                onEditStateChange({
                  ...editState,
                  endTime: value ? value.format('HH:mm') : editState.endTime,
                })
              }
              disabled={loading}
              style={fullWidthStyle}
              relativeToTime={
                editState.startTime ? dayjs(editState.startTime, 'HH:mm') : null
              }
            />
          </div>

          {/* Duration (calculated) */}
          <span
            css={css`
              ${textStyles}
              width: ${COLUMN_WIDTHS.DURATION}px;
            `}
          >
            {calculateDuration(editState.startTime, editState.endTime)}
          </span>

          {/* Divisions dropdown */}
          <div
            css={css`
              flex: 1;
              min-width: ${COLUMN_WIDTHS.DIVISIONS}px;
            `}
          >
            <Select
              ref={divisionsSelectRef}
              mode='multiple'
              value={editState.divisions}
              onChange={(values: string[]) =>
                onEditStateChange({ ...editState, divisions: values })
              }
              placeholder='All Divisions'
              disabled={loading}
              css={selectStyles}
              style={fullWidthStyle}
              maxTagCount='responsive'
              allowClear
              popupClassName='dark-select-popup'
              // eslint-disable-next-line react/no-unstable-nested-components
              dropdownRender={(menu) => (
                <>
                  <div
                    css={css`
                      padding: 8px 12px;
                      cursor: pointer;
                      color: ${colors.PRIMARY};
                      border-bottom: 1px solid ${colors.SECONDARY_200};
                      &:hover {
                        background-color: ${colors.SECONDARY_400};
                      }
                    `}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const allIds = divisions.map((d) => d.id)
                      const allSelected =
                        editState.divisions.length === divisions.length
                      onEditStateChange({
                        ...editState,
                        divisions: allSelected ? [] : allIds,
                      })
                      // Close dropdown after selecting all
                      if (!allSelected) {
                        divisionsSelectRef.current?.blur()
                      }
                    }}
                  >
                    All Divisions
                  </div>
                  {menu}
                </>
              )}
            >
              {divisions.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </div>

          {/* Actions */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              flex-shrink: 0;
            `}
          >
            <button
              type='button'
              onClick={onSave}
              disabled={loading}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 16px;
                background: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover:not(:disabled) {
                  background: ${colors.PRIMARY_HOVER};
                }

                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
              aria-label='Save time slot'
            >
              <SaveOutlined
                style={{ fontSize: 16, color: colors.SECONDARY_300 }} // eslint-disable-line react-native/no-inline-styles
              />
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 20.3px;
                  color: ${colors.SECONDARY_300};
                `}
              >
                Save
              </span>
            </button>
            <Tooltip title='Cancel' placement='top'>
              <button
                type='button'
                onClick={onCancel}
                disabled={loading}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 32px;
                  height: 32px;
                  background: transparent;
                  border: 1px solid ${colors.NEUTRAL_50};
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.2s ease;

                  &:hover:not(:disabled) {
                    border-color: ${colors.ERROR_200};
                    background: ${colors.ERROR_200}20;
                  }

                  &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                  }
                `}
                aria-label='Cancel editing'
              >
                <CloseOutlined
                  style={{ fontSize: 14, color: colors.NEUTRAL_50 }} // eslint-disable-line react-native/no-inline-styles
                />
              </button>
            </Tooltip>
          </div>
        </div>
      </>
    )
  }

  // Display mode
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 8px 16px 8px 48px;
        background-color: ${colors.SECONDARY_400};
        width: 100%;

        cursor: pointer;

        &:hover {
          background-color: ${'#212935'};

          & > div:last-child {
            opacity: 1;
          }
        }
      `}
      onClick={onEdit}
    >
      {/* Slot number */}
      <span
        css={css`
          ${textStyles}
          width: ${COLUMN_WIDTHS.SLOT}px;
          color: ${colors.NEUTRAL_0};
          font-weight: 400;
        `}
      >
        {slotNumber}
      </span>

      {/* Frequency */}
      <span
        css={css`
          ${textStyles}
          width: ${COLUMN_WIDTHS.FREQUENCY}px;
        `}
      >
        {timeSlot.slot_date && timeSlot.recurrence_type === 'specific'
          ? formatSlotDate(timeSlot.slot_date)
          : RECURRENCE_TYPES.find((rt) => rt.value === timeSlot.recurrence_type)
              ?.label || 'Weekly'}
      </span>

      {/* Days of week */}
      <span
        css={css`
          ${textStyles}
          width: ${COLUMN_WIDTHS.DAYS}px;
        `}
      >
        {timeSlot.slot_date && timeSlot.recurrence_type === 'specific'
          ? getDayNameFromDate(timeSlot.slot_date)
          : formatDaysDisplay(timeSlot.days)}
      </span>

      {/* Start time */}
      <span
        css={css`
          ${textStyles}
          width: ${COLUMN_WIDTHS.START_TIME}px;
        `}
      >
        {formatTimeDisplay(times.start)}
      </span>

      {/* End time */}
      <span
        css={css`
          ${textStyles}
          width: ${COLUMN_WIDTHS.END_TIME}px;
        `}
      >
        {formatTimeDisplay(times.end)}
      </span>

      {/* Duration */}
      <span
        css={css`
          ${textStyles}
          width: ${COLUMN_WIDTHS.DURATION}px;
        `}
      >
        {calculateDuration(times.start, times.end)}
      </span>

      {/* Divisions */}
      <span
        css={css`
          ${textStyles}
          flex: 1;
          min-width: ${COLUMN_WIDTHS.DIVISIONS}px;
        `}
        title={
          !timeSlot.divisions || timeSlot.divisions.length === 0
            ? 'All Divisions'
            : timeSlot.divisions
                .map((id) => divisions.find((d) => d.id === id)?.name || id)
                .join(', ')
        }
      >
        {!timeSlot.divisions || timeSlot.divisions.length === 0
          ? 'All Divisions'
          : timeSlot.divisions.length === divisions.length
          ? 'All Divisions'
          : timeSlot.divisions
              .map((id) => divisions.find((d) => d.id === id)?.name || id)
              .join(', ')}
      </span>

      {/* Action icons */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
          opacity: 0;
          transition: opacity 0.15s ease;
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title='Edit Time Slot' placement='top'>
          <button
            type='button'
            onClick={onEdit}
            css={iconButtonStyles}
            aria-label='Edit time slot'
          >
            <EditOutlined style={iconFontStyle} />
          </button>
        </Tooltip>
        {onDuplicate && (
          <Tooltip title='Duplicate Time Slot' placement='top'>
            <button
              type='button'
              onClick={onDuplicate}
              css={iconButtonStyles}
              aria-label='Duplicate time slot'
            >
              <CopyOutlined style={iconFontStyle} />
            </button>
          </Tooltip>
        )}
        <Tooltip title='Delete Time Slot' placement='top'>
          <button
            type='button'
            onClick={onDelete}
            css={css`
              ${iconButtonStyles}
              color: ${colors.ERROR_200};

              &:hover {
                color: ${colors.ERROR_300};
              }
            `}
            aria-label='Delete time slot'
          >
            <DeleteOutlined style={iconFontStyle} />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}

export default TimeSlotRow
